import { useState, useEffect } from "react";
import { NavShell } from "@/components/NavShell";
import { Calendar as CalendarIcon, Plus, ChevronLeft, ChevronRight, Trash2, Edit3, X, Check, Clock, Twitter, Linkedin, Instagram, FileText, Mail, Globe } from "lucide-react";

const PLATFORMS = [
  { id: "twitter", label: "Twitter/X", icon: Twitter, color: "text-sky-400 bg-sky-400/10 border-sky-400/20" },
  { id: "linkedin", label: "LinkedIn", icon: Linkedin, color: "text-blue-400 bg-blue-400/10 border-blue-400/20" },
  { id: "instagram", label: "Instagram", icon: Instagram, color: "text-pink-400 bg-pink-400/10 border-pink-400/20" },
  { id: "blog", label: "Blog", icon: FileText, color: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20" },
  { id: "email", label: "Email", icon: Mail, color: "text-amber-400 bg-amber-400/10 border-amber-400/20" },
  { id: "other", label: "Other", icon: Globe, color: "text-gray-400 bg-gray-400/10 border-gray-400/20" },
];

const STATUS_STYLES = {
  scheduled: "bg-violet-500/10 text-violet-300 border-violet-500/20",
  published: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20",
  draft: "bg-gray-500/10 text-gray-300 border-gray-500/20",
};

const getPlatform = (id) => PLATFORMS.find(p => p.id === id) || PLATFORMS[5];

export default function CalendarPage({ user, apiClient, onLogout }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [contentList, setContentList] = useState([]);

  // Form state
  const [formTitle, setFormTitle] = useState("");
  const [formPlatform, setFormPlatform] = useState("twitter");
  const [formDate, setFormDate] = useState("");
  const [formTime, setFormTime] = useState("09:00");
  const [formNotes, setFormNotes] = useState("");
  const [formStatus, setFormStatus] = useState("scheduled");
  const [formContentId, setFormContentId] = useState("");

  useEffect(() => {
    loadEvents();
    loadContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth]);

  const monthStr = `${currentMonth.year}-${String(currentMonth.month + 1).padStart(2, '0')}`;

  const loadEvents = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/calendar?month=${monthStr}`);
      setEvents(res.data.events);
    } catch {} finally { setLoading(false); }
  };

  const loadContent = async () => {
    try {
      const res = await apiClient.get("/history");
      setContentList(res.data.history.slice(0, 20));
    } catch {}
  };

  const resetForm = () => {
    setFormTitle(""); setFormPlatform("twitter"); setFormDate(""); setFormTime("09:00");
    setFormNotes(""); setFormStatus("scheduled"); setFormContentId(""); setEditingEvent(null);
  };

  const openNewForm = (dateStr) => {
    resetForm();
    setFormDate(dateStr || "");
    setShowForm(true);
  };

  const openEditForm = (event) => {
    setEditingEvent(event);
    setFormTitle(event.title);
    setFormPlatform(event.platform);
    setFormDate(event.scheduled_date);
    setFormTime(event.scheduled_time || "09:00");
    setFormNotes(event.notes || "");
    setFormStatus(event.status);
    setFormContentId(event.content_id || "");
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!formTitle.trim() || !formDate) return;
    try {
      if (editingEvent) {
        await apiClient.put(`/calendar/${editingEvent.id}`, {
          title: formTitle, platform: formPlatform, scheduled_date: formDate,
          scheduled_time: formTime, notes: formNotes, status: formStatus,
        });
      } else {
        await apiClient.post("/calendar", {
          title: formTitle, content_id: formContentId || null, platform: formPlatform,
          scheduled_date: formDate, scheduled_time: formTime, notes: formNotes, status: formStatus,
        });
      }
      setShowForm(false);
      resetForm();
      loadEvents();
    } catch {}
  };

  const deleteEvent = async (id) => {
    try {
      await apiClient.delete(`/calendar/${id}`);
      setEvents(e => e.filter(ev => ev.id !== id));
    } catch {}
  };

  const toggleStatus = async (event) => {
    const nextStatus = event.status === "scheduled" ? "published" : event.status === "published" ? "draft" : "scheduled";
    try {
      await apiClient.put(`/calendar/${event.id}`, { status: nextStatus });
      setEvents(evts => evts.map(e => e.id === event.id ? { ...e, status: nextStatus } : e));
    } catch {}
  };

  // Calendar rendering
  const daysInMonth = new Date(currentMonth.year, currentMonth.month + 1, 0).getDate();
  const firstDayOfWeek = new Date(currentMonth.year, currentMonth.month, 1).getDay();
  const monthName = new Date(currentMonth.year, currentMonth.month).toLocaleDateString('en', { month: 'long', year: 'numeric' });
  const today = new Date().toISOString().slice(0, 10);

  const prevMonth = () => {
    setCurrentMonth(prev => prev.month === 0 ? { year: prev.year - 1, month: 11 } : { year: prev.year, month: prev.month - 1 });
  };
  const nextMonth = () => {
    setCurrentMonth(prev => prev.month === 11 ? { year: prev.year + 1, month: 0 } : { year: prev.year, month: prev.month + 1 });
  };

  const getEventsForDate = (day) => {
    const dateStr = `${currentMonth.year}-${String(currentMonth.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.scheduled_date === dateStr);
  };

  return (
    <NavShell user={user} onLogout={onLogout} active="calendar">
      <div className="p-6 md:p-8 max-w-6xl mx-auto space-y-6" data-testid="calendar-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight mb-1" style={{ fontFamily: 'Outfit' }}>
              <span className="brand-gradient-text">Content Calendar</span>
            </h1>
            <p className="text-gray-500 text-sm">Plan and schedule your content across platforms.</p>
          </div>
          <button
            onClick={() => openNewForm(today)}
            className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white px-5 py-2.5 rounded-lg text-xs font-medium tracking-wide uppercase transition-colors duration-200 active:scale-95 shadow-[0_0_20px_rgba(139,92,246,0.3)]"
            data-testid="new-event-btn"
          >
            <Plus className="w-4 h-4" /> Schedule Content
          </button>
        </div>

        {/* Month navigation */}
        <div className="flex items-center justify-between">
          <button onClick={prevMonth} className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors duration-200" data-testid="prev-month-btn">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-xl font-semibold" style={{ fontFamily: 'Outfit' }} data-testid="month-title">{monthName}</h2>
          <button onClick={nextMonth} className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors duration-200" data-testid="next-month-btn">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Calendar grid */}
        <div className="bg-gray-900/50 border border-white/5 rounded-xl overflow-hidden" data-testid="calendar-grid">
          {/* Day headers */}
          <div className="grid grid-cols-7 border-b border-white/5">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
              <div key={day} className="px-2 py-3 text-xs font-semibold tracking-wide uppercase text-gray-500 text-center">
                {day}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7">
            {/* Empty cells for days before month start */}
            {Array.from({ length: firstDayOfWeek }, (_, i) => (
              <div key={`empty-${i}`} className="min-h-[100px] border-b border-r border-white/5 bg-black/20" />
            ))}

            {/* Day cells */}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const dateStr = `${currentMonth.year}-${String(currentMonth.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const dayEvents = getEventsForDate(day);
              const isToday = dateStr === today;

              return (
                <div
                  key={day}
                  className={`min-h-[100px] border-b border-r border-white/5 p-1.5 cursor-pointer hover:bg-white/[0.02] transition-colors duration-200 ${isToday ? 'bg-violet-500/5' : ''}`}
                  onClick={() => openNewForm(dateStr)}
                  data-testid={`day-${day}`}
                >
                  <div className={`text-xs font-medium mb-1 w-6 h-6 flex items-center justify-center rounded-full ${isToday ? 'bg-violet-500 text-white' : 'text-gray-400'}`}>
                    {day}
                  </div>
                  <div className="space-y-1">
                    {dayEvents.slice(0, 3).map(evt => {
                      const plat = getPlatform(evt.platform);
                      return (
                        <div
                          key={evt.id}
                          onClick={(e) => { e.stopPropagation(); openEditForm(evt); }}
                          className={`text-[10px] px-1.5 py-1 rounded border truncate cursor-pointer hover:opacity-80 transition-opacity duration-200 ${plat.color}`}
                          data-testid={`event-${evt.id}`}
                        >
                          {evt.title}
                        </div>
                      );
                    })}
                    {dayEvents.length > 3 && (
                      <div className="text-[10px] text-gray-500 px-1">+{dayEvents.length - 3} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Upcoming events list */}
        <div>
          <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>Upcoming</h3>
          {events.length === 0 ? (
            <div className="text-center py-12 bg-gray-900/30 border border-white/5 rounded-xl" data-testid="calendar-empty">
              <CalendarIcon className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No scheduled content this month. Click a date or "Schedule Content" to get started.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {events.map(evt => {
                const plat = getPlatform(evt.platform);
                const PlatIcon = plat.icon;
                return (
                  <div key={evt.id} className="bg-gray-900/50 border border-white/5 rounded-xl p-4 flex items-center gap-4 hover:border-violet-500/20 transition-colors duration-300" data-testid={`upcoming-${evt.id}`}>
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border ${plat.color}`}>
                      <PlatIcon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-medium truncate">{evt.title}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium uppercase tracking-wider ${STATUS_STYLES[evt.status] || STATUS_STYLES.draft}`}>
                          {evt.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>{new Date(evt.scheduled_date + 'T00:00:00').toLocaleDateString('en', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {evt.scheduled_time}</span>
                        {evt.notes && <span className="truncate max-w-[200px]">- {evt.notes}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => toggleStatus(evt)} className="p-1.5 rounded-lg hover:bg-white/5 text-gray-500 hover:text-emerald-400 transition-colors duration-200" title="Toggle status" data-testid={`toggle-${evt.id}`}>
                        <Check className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => openEditForm(evt)} className="p-1.5 rounded-lg hover:bg-white/5 text-gray-500 hover:text-violet-400 transition-colors duration-200" data-testid={`edit-${evt.id}`}>
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => deleteEvent(evt.id)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-colors duration-200" data-testid={`del-${evt.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Modal form */}
        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => { setShowForm(false); resetForm(); }} data-testid="event-modal">
            <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-md space-y-5 animate-fade-in-up" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
                  {editingEvent ? "Edit Event" : "Schedule Content"}
                </h3>
                <button onClick={() => { setShowForm(false); resetForm(); }} className="p-1 text-gray-500 hover:text-white transition-colors duration-200" data-testid="close-modal-btn">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div>
                <label className="block text-xs font-semibold tracking-wide text-gray-400 uppercase mb-2">Title</label>
                <input
                  value={formTitle}
                  onChange={e => setFormTitle(e.target.value)}
                  className="w-full bg-black/20 border border-white/10 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 rounded-lg h-10 px-4 text-sm text-white placeholder:text-gray-500 outline-none transition-colors duration-200"
                  placeholder="What are you publishing?"
                  data-testid="event-title-input"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold tracking-wide text-gray-400 uppercase mb-2">Platform</label>
                <div className="grid grid-cols-3 gap-2">
                  {PLATFORMS.map(p => (
                    <button
                      key={p.id}
                      onClick={() => setFormPlatform(p.id)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-colors duration-200 ${formPlatform === p.id ? p.color : 'bg-gray-800/50 border-white/5 text-gray-500 hover:border-white/10'}`}
                      data-testid={`platform-${p.id}`}
                    >
                      <p.icon className="w-3.5 h-3.5" /> {p.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold tracking-wide text-gray-400 uppercase mb-2">Date</label>
                  <input
                    type="date"
                    value={formDate}
                    onChange={e => setFormDate(e.target.value)}
                    className="w-full bg-black/20 border border-white/10 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 rounded-lg h-10 px-3 text-sm text-white outline-none transition-colors duration-200"
                    data-testid="event-date-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold tracking-wide text-gray-400 uppercase mb-2">Time</label>
                  <input
                    type="time"
                    value={formTime}
                    onChange={e => setFormTime(e.target.value)}
                    className="w-full bg-black/20 border border-white/10 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 rounded-lg h-10 px-3 text-sm text-white outline-none transition-colors duration-200"
                    data-testid="event-time-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold tracking-wide text-gray-400 uppercase mb-2">Status</label>
                <div className="flex gap-2">
                  {["draft", "scheduled", "published"].map(s => (
                    <button
                      key={s}
                      onClick={() => setFormStatus(s)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize border transition-colors duration-200 ${formStatus === s ? STATUS_STYLES[s] : 'bg-gray-800/50 border-white/5 text-gray-500'}`}
                      data-testid={`status-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {!editingEvent && contentList.length > 0 && (
                <div>
                  <label className="block text-xs font-semibold tracking-wide text-gray-400 uppercase mb-2">Link Content <span className="normal-case text-gray-600">(optional)</span></label>
                  <select
                    value={formContentId}
                    onChange={e => { setFormContentId(e.target.value); if (!formTitle && e.target.value) { const c = contentList.find(x => x.id === e.target.value); if (c) setFormTitle(c.topic); }}}
                    className="w-full bg-black/20 border border-white/10 focus:border-violet-500 rounded-lg h-10 px-3 text-sm text-white outline-none transition-colors duration-200"
                    data-testid="event-content-select"
                  >
                    <option value="">None</option>
                    {contentList.map(c => (
                      <option key={c.id} value={c.id}>{c.topic} ({c.template})</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold tracking-wide text-gray-400 uppercase mb-2">Notes <span className="normal-case text-gray-600">(optional)</span></label>
                <textarea
                  value={formNotes}
                  onChange={e => setFormNotes(e.target.value)}
                  rows={2}
                  className="w-full bg-black/20 border border-white/10 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-gray-500 outline-none resize-none transition-colors duration-200"
                  placeholder="Any notes about this post..."
                  data-testid="event-notes-input"
                />
              </div>

              <button
                onClick={handleSubmit}
                disabled={!formTitle.trim() || !formDate}
                className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white py-3 rounded-lg text-sm font-medium tracking-wide uppercase transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_20px_rgba(139,92,246,0.3)]"
                data-testid="save-event-btn"
              >
                {editingEvent ? "Update Event" : "Schedule"}
              </button>
            </div>
          </div>
        )}
      </div>
    </NavShell>
  );
}
